/** Brand Brain — types + API bindings. */
import { http } from "@/shared/api/http";

export interface CompanyIdentity {
  company_name: string;
  tagline: string;
  mission: string;
  vision: string;
  core_values: string[];
  brand_story: string;
}
export interface BrandVoice {
  tone: string;
  personality: string;
  writing_style: string;
  reading_level: string;
  humor: string;
  emoji_usage: string;
  formatting_preferences: string;
}
export interface AudienceProfile {
  primary_audience: string;
  secondary_audience: string;
  demographics: string;
  interests: string[];
  pain_points: string[];
  goals: string[];
}
export interface ProductItem { name: string; description: string; price_notes: string }
export interface ServiceItem { name: string; description: string; price_notes: string }
export interface ProductsServices {
  products: ProductItem[];
  services: ServiceItem[];
  categories: string[];
  pricing_notes: string;
  promotions: string[];
}
export interface ObjectionHandler { objection: string; response: string }
export interface Marketing {
  primary_cta: string;
  secondary_cta: string;
  marketing_angles: string[];
  value_propositions: string[];
  objection_handling: ObjectionHandler[];
}
export interface FaqItem { question: string; approved_answer: string }
export interface CompetitorItem { name: string; notes: string }
export interface Knowledge {
  faqs: FaqItem[];
  competitors: CompetitorItem[];
  industry_notes: string;
  seasonal_notes: string;
}
export interface ContentRules {
  approved_words: string[];
  banned_words: string[];
  required_phrases: string[];
  legal_disclaimers: string[];
  compliance_notes: string;
}
export interface VisualIdentity {
  primary_color: string;
  secondary_color: string;
  logo_key: string;
  typography: string;
  visual_style: string;
  image_style: string;
  photography_direction: string;
}
export interface AISettings {
  preferred_creativity: "conservative" | "balanced" | "bold";
  preferred_length: "short" | "medium" | "long";
  preferred_platforms: string[];
  default_prompt_style: string;
}

export interface BrandBrain {
  id: string;
  workspace_id: string;
  status: "draft" | "pending_review" | "approved";
  approved_at?: string | null;
  approved_by?: string | null;
  source?: string;
  identity: CompanyIdentity;
  voice: BrandVoice;
  audience: AudienceProfile;
  products_services: ProductsServices;
  marketing: Marketing;
  knowledge: Knowledge;
  content_rules: ContentRules;
  visual: VisualIdentity;
  ai_settings: AISettings;
}

export type SectionKey =
  | "identity" | "voice" | "audience" | "products_services"
  | "marketing" | "knowledge" | "content_rules" | "visual" | "ai_settings";

export interface HealthDimension {
  key: string;
  label: string;
  complete: boolean;
  detail: string;
}
export interface BrandHealth {
  score: number;
  ready_for_ai: boolean;
  dimensions: HealthDimension[];
}

export const fetchBrandBrain = (workspaceId: string): Promise<BrandBrain> =>
  http<BrandBrain>(`/workspaces/${workspaceId}/brand-brain`);

export const patchBrandBrainSection = (
  workspaceId: string,
  section: SectionKey,
  data: Record<string, unknown>,
): Promise<BrandBrain> =>
  http<BrandBrain>(`/workspaces/${workspaceId}/brand-brain`, {
    method: "PATCH",
    body: { section, data },
  });

export const fetchBrandHealth = (workspaceId: string): Promise<BrandHealth> =>
  http<BrandHealth>(`/workspaces/${workspaceId}/brand-brain/health`);

// ── Patch 34.1.5 · Approval lifecycle ─────────────────────────────
export const submitBrandBrainForReview = (workspaceId: string): Promise<BrandBrain> =>
  http<BrandBrain>(`/workspaces/${workspaceId}/brand-brain/submit-for-review`, {
    method: "POST",
  });

export const approveBrandBrain = (workspaceId: string): Promise<BrandBrain> =>
  http<BrandBrain>(`/workspaces/${workspaceId}/brand-brain/approve`, {
    method: "POST",
  });
